import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import {
  createDocument,
  createFolder,
  deleteDocument,
  deleteFile,
  deleteFolder,
  fetchDocument,
  fetchDocuments,
  fetchFiles,
  fetchFolders,
  updateDocument,
  updateFolder,
  uploadFile,
} from '../../redux/workspace'
import {
  selectDocumentsByFolder,
  selectFilesByDocument,
  selectFilesByFolder,
  selectFolders,
  selectFoldersStatus,
  selectOpenDocument,
} from '../../redux/workspace'
import WorkspaceFolderTree from '../../Components/WorkspaceFolderTree/WorkspaceFolderTree'
import WorkspaceEditor from '../../Components/WorkspaceEditor/WorkspaceEditor'
import WorkspaceFileAttachments from '../../Components/WorkspaceFileAttachments/WorkspaceFileAttachments'
import WorkspaceLinkedTasks from '../../Components/WorkspaceLinkedTasks/WorkspaceLinkedTasks'
import {
  SkeletonWorkspaceFolderTree,
  SkeletonWorkspaceDocList,
  SkeletonWorkspaceEditor,
} from '../../Components/Skeleton/Skeleton'
import styles from './Workspace.module.css'

const WorkspaceScreen = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { documentId } = useParams<{ documentId?: string }>()
  const bootstrappedRef = useRef(false)
  const requestedDocumentRef = useRef<string | null>(null)

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [draggedDocumentId, setDraggedDocumentId] = useState<string | null>(null)

  const folders = useAppSelector(selectFolders)
  const foldersStatus = useAppSelector(selectFoldersStatus)
  const documents = useAppSelector(selectDocumentsByFolder(selectedFolderId))
  const files = useAppSelector(
    documentId ? selectFilesByDocument(documentId) : selectFilesByFolder(selectedFolderId)
  )
  const openDocument = useAppSelector(
    documentId ? selectOpenDocument(documentId) : () => undefined
  )

  useEffect(() => {
    // In React StrictMode (dev), effects can run twice.
    // Guard + sequential loading avoids duplicate parallel Cosmos bursts.
    if (bootstrappedRef.current) return
    bootstrappedRef.current = true

    const loadInitialWorkspaceData = async () => {
      await dispatch(fetchFolders())
      await dispatch(fetchDocuments())
      await dispatch(fetchFiles())
    }

    void loadInitialWorkspaceData()
  }, [dispatch])

  useEffect(() => {
    if (!documentId || openDocument) return
    if (requestedDocumentRef.current === documentId) return

    requestedDocumentRef.current = documentId
    void dispatch(fetchDocument(documentId))
  }, [documentId, openDocument, dispatch])

  const handleSelectFolder = (id: string | null) => {
    setSelectedFolderId(id)
    if (documentId) navigate('/workspace')
  }

  const handleCreateDocument = async () => {
    const result = await dispatch(
      createDocument({ title: 'Untitled', folderId: selectedFolderId })
    )
    if (createDocument.fulfilled.match(result)) {
      navigate(`/workspace/${result.payload.id}`)
    }
  }

  const handleSave = useCallback(
    (patch: { title?: string; content?: string }) => {
      if (!documentId) return
      void dispatch(updateDocument({ id: documentId, patch }))
    },
    [documentId, dispatch]
  )

  const handleDeleteDocument = async (id: string, title: string) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return
    await dispatch(deleteDocument(id))
    if (documentId === id) navigate('/workspace')
  }

  const handleUploadFile = async (file: File) => {
    setUploading(true)
    const formData = new FormData()
    formData.append('file', file)
    if (selectedFolderId) formData.append('folderId', selectedFolderId)
    if (documentId) formData.append('documentId', documentId)
    await dispatch(uploadFile(formData))
    setUploading(false)
  }

  const handleDropDocumentToFolder = async (docId: string, folderId: string | null) => {
    const document = documents.find((d) => d.id === docId) ?? openDocument
    if (!document) return
    if ((document.folderId ?? null) === folderId) return

    await dispatch(updateDocument({ id: docId, patch: { folderId } }))
    setDraggedDocumentId(null)
  }

  const handleLinkTaskToDocument = async (taskId: string) => {
    if (!openDocument) return
    if (openDocument.linkedTaskIds.includes(taskId)) return

    await dispatch(
      updateDocument({
        id: openDocument.id,
        patch: { linkedTaskIds: [...openDocument.linkedTaskIds, taskId] },
      })
    )
  }

  const handleUnlinkTaskFromDocument = async (taskId: string) => {
    if (!openDocument) return

    await dispatch(
      updateDocument({
        id: openDocument.id,
        patch: { linkedTaskIds: openDocument.linkedTaskIds.filter((id) => id !== taskId) },
      })
    )
  }

  return (
    <div className={styles.container}>
      {/* Sidebar */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <span className={styles.sidebarTitle}>Workspace</span>
        </div>

        {foldersStatus === 'loading' && folders.length === 0 ? (
          <SkeletonWorkspaceFolderTree />
        ) : (
          <WorkspaceFolderTree
            folders={folders}
            selectedFolderId={selectedFolderId}
            draggedDocumentId={draggedDocumentId}
            onSelectFolder={handleSelectFolder}
            onCreateFolder={(name, parentId) =>
              void dispatch(createFolder({ name, parentFolderId: parentId }))
            }
            onRenameFolder={(id, name) =>
              void dispatch(updateFolder({ id, patch: { name } }))
            }
            onDeleteFolder={(id) => void dispatch(deleteFolder(id))}
            onDropDocument={handleDropDocumentToFolder}
          />
        )}

        <div className={styles.docListSection}>
          <div className={styles.docListHeader}>
            <span className={styles.docListLabel}>Documents</span>
            <button
              type="button"
              className={styles.newDocBtn}
              title="New document"
              onClick={handleCreateDocument}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {foldersStatus === 'loading' && documents.length === 0 ? (
            <SkeletonWorkspaceDocList count={4} />
          ) : documents.length === 0 ? (
            <p className={styles.emptyHint}>No documents yet.</p>
          ) : (
            <ul className={styles.docList}>
              {documents.map((doc) => (
                <li key={doc.id}>
                  <button
                    type="button"
                    className={`${styles.docItem} ${documentId === doc.id ? styles.docItemActive : ''}`}
                    onClick={() => navigate(`/workspace/${doc.id}`)}
                    draggable
                    onDragStart={() => setDraggedDocumentId(doc.id)}
                    onDragEnd={() => setDraggedDocumentId(null)}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden className={styles.docIcon}>
                      <path
                        d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinejoin="round"
                      />
                      <path d="M14 2v6h6M8 13h8M8 17h5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                    </svg>
                    <span className={styles.docTitle}>{doc.title || 'Untitled'}</span>
                    <button
                      type="button"
                      className={styles.docDeleteBtn}
                      title="Delete document"
                      onClick={(e) => {
                        e.stopPropagation()
                        void handleDeleteDocument(doc.id, doc.title)
                      }}
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </button>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>

      {/* Main editor area */}
      <main className={styles.main}>
        {!documentId ? (
          <div className={styles.emptyState}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
              <path d="M14 2v6h6M8 13h8M8 17h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <p>Select a document or create a new one</p>
            <button type="button" className={styles.emptyStateBtn} onClick={handleCreateDocument}>
              New document
            </button>
          </div>
        ) : openDocument ? (
          <div className={styles.editorWrapper}>
            <div className={styles.editorScrollArea}>
              <WorkspaceLinkedTasks
                document={openDocument}
                onLink={handleLinkTaskToDocument}
                onUnlink={handleUnlinkTaskFromDocument}
              />
              <WorkspaceFileAttachments
                files={files.filter((f) => f.linkedTaskIds.length === 0)}
                onUpload={handleUploadFile}
                onDelete={(id, fileName) => {
                  if (window.confirm(`Remove "${fileName}"?`)) {
                    void dispatch(deleteFile(id))
                  }
                }}
                uploading={uploading}
              />
              <WorkspaceEditor
                key={openDocument.id}
                document={openDocument}
                onSave={handleSave}
              />
            </div>
          </div>
        ) : (
          <div className={styles.editorWrapper}>
            <div className={styles.editorScrollArea}>
              <SkeletonWorkspaceEditor />
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

export default WorkspaceScreen
